import { fmtDate } from "utils/date";
import React, { useState, useEffect, useRef } from "react";
import {
  Card, CardHeader, CardBody, CardTitle,
  Table, Row, Col, Button, Input, FormGroup, Label, Spinner,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import axiosInstance from "service/axios";
import { CicloService } from "service/CicloService";
import { SalonService } from "service/SalonService";
import FSelect from "components/Select/FSelect";
import FDateInput from "components/DateInput/FDateInput";
import CustomPagination from "components/Pagination/Pagination";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

if (pdfFonts?.pdfMake) pdfMake.vfs = pdfFonts.pdfMake.vfs;
else if (pdfFonts) pdfMake.vfs = pdfFonts.vfs;

const cicloService = new CicloService();
const salonService = new SalonService();

const TIPOS = [
  { value: "F", label: "Faltas" },
  { value: "T", label: "Tardanzas" },
  { value: "A", label: "Asistencias" },
];

function FaltasVirtual() {
  const today = new Date().toISOString().split("T")[0];
  const firstOfMonth = new Date(new Date().setDate(1)).toISOString().split("T")[0];

  const notificationAlertRef = useRef(null);
  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [listCiclos, setListCiclos] = useState([]);
  const [listSalones, setListSalones] = useState([]);
  const [ciclo, setCiclo] = useState("");
  const [salon, setSalon] = useState("");
  const [tipo, setTipo] = useState("F");
  const [fechaDesde, setFechaDesde] = useState(firstOfMonth);
  const [fechaHasta, setFechaHasta] = useState(today);

  const [listReporte, setListReporte] = useState([]);
  const [listFiltered, setListFiltered] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [cicloNombre, setCicloNombre] = useState("");
  const [loading, setLoading] = useState(false);
  const [mensaje, setMensaje] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);

  useEffect(() => {
    getCiclos();
    getSalones();
  }, []);

  const getCiclos = async () => {
    try {
      const data = await cicloService.getCiclos();
      const items = Array.isArray(data) ? data : data?.data || [];
      setListCiclos(items);
      if (items.length > 0) setCiclo(items[0]._id);
    } catch (error) {
      console.error("Error cargando ciclos:", error);
    }
  };

  const getSalones = async () => {
    try {
      const data = await salonService.getSalones();
      const items = Array.isArray(data) ? data : data?.data || [];
      setListSalones(items);
    } catch (error) {
      console.error("Error cargando salones:", error);
    }
  };

  const consultar = async () => {
    if (!ciclo || !salon || !fechaDesde || !fechaHasta) {
      notify("warning", "Completa todos los filtros para consultar");
      return;
    }
    setLoading(true);
    setMensaje("");
    setListReporte([]);
    setListFiltered([]);
    setSearchQuery("");
    try {
      const response = await axiosInstance.get("/asistenciapresencial/reporteasistencia", {
        params: { ciclo, salon, tiporeport: tipo, fechadesde: fechaDesde, fechahasta: fechaHasta },
      });
      const items = Array.isArray(response.data) ? response.data : response.data?.data || [];
      const sorted = [...items].sort((a, b) =>
        (a.alumno?.apellidopaterno || "").localeCompare(b.alumno?.apellidopaterno || "")
      );
      setListReporte(sorted);
      setListFiltered(sorted);
      const cicloObj = listCiclos.find((c) => c._id === ciclo);
      setCicloNombre(cicloObj?.tipociclo || cicloObj?.denominacion || "");
      if (sorted.length === 0) setMensaje("No se encontraron registros.");
    } catch (error) {
      console.error("Error consultando reporte:", error);
      setMensaje("Error al obtener el reporte.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (!query) { setListFiltered(listReporte); setCurrentPage(1); return; }
    const q = query.toLowerCase();
    setListFiltered(listReporte.filter((item) =>
      (item.alumno?.documentoidentidad || "").includes(q) ||
      (item.alumno?.nombres || "").toLowerCase().includes(q) ||
      (item.alumno?.apellidopaterno || "").toLowerCase().includes(q) ||
      (item.alumno?.apellidomaterno || "").toLowerCase().includes(q)
    ));
    setCurrentPage(1);
  };

  const exportarPDF = () => {
    const tipoLabel = TIPOS.find((t) => t.value === tipo)?.label || tipo;
    const docDefinition = {
      content: [
        { text: "Academia FALCONS - Reporte de Asistencia", style: "header" },
        { text: `Ciclo: ${cicloNombre} | Tipo: ${tipoLabel} | Desde: ${fechaDesde} | Hasta: ${fechaHasta}`, style: "subheader" },
        { text: " " },
        {
          table: {
            headerRows: 1,
            widths: ["auto", "auto", "*", "auto", "auto", "auto"],
            body: [
              ["N°", "Fecha", "Apellidos y Nombres", "DNI", "H.I.M.", "Estado"],
              ...listFiltered.map((row, i) => [
                i + 1,
                fmtDate(row.fecha),
                `${row.alumno?.apellidopaterno || ""} ${row.alumno?.apellidomaterno || ""}, ${row.alumno?.nombres || ""}`,
                row.alumno?.documentoidentidad || "-",
                row.horaIngresoManiana || "-",
                row.estado || "-",
              ]),
            ],
          },
        },
      ],
      styles: {
        header: { fontSize: 14, bold: true, margin: [0, 0, 0, 4] },
        subheader: { fontSize: 10, margin: [0, 0, 0, 8], color: "#555" },
      },
    };
    pdfMake.createPdf(docDefinition).download(`reporte_asistencia_${tipo}_${fechaDesde}.pdf`);
  };

  const indexOfLast = currentPage * itemsPerPage;
  const currentItems = listFiltered.slice(indexOfLast - itemsPerPage, indexOfLast);

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              {/* Zona 1: Header */}
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Control de Asistencia y Faltas</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button color="link" className="btn-icon btn-round">
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                {/* Zona 2: Barra de filtros principal */}
                <Row className="align-items-end mb-4">
                  <Col md="3">
                    <FormGroup className="mb-0">
                      <Label for="ciclo">Ciclo Académico</Label>
                      <FSelect
                        id="ciclo"
                        value={ciclo}
                        onChange={(e) => setCiclo(e.target.value)}
                        options={listCiclos.map((c) => ({ value: c._id, label: c.tipociclo || c.denominacion }))}
                        placeholder="Seleccionar..."
                      />
                    </FormGroup>
                  </Col>
                  <Col md="2">
                    <FormGroup className="mb-0">
                      <Label for="salon">Salón / Aula</Label>
                      <FSelect
                        id="salon"
                        value={salon}
                        onChange={(e) => setSalon(e.target.value)}
                        options={listSalones.map((s) => ({ value: s._id, label: s.denominacion }))}
                        placeholder="Seleccionar..."
                      />
                    </FormGroup>
                  </Col>
                  <Col md="2">
                    <FormGroup className="mb-0">
                      <Label for="tipo">Tipo de Registro</Label>
                      <FSelect
                        id="tipo"
                        value={tipo}
                        onChange={(e) => setTipo(e.target.value)}
                        options={TIPOS.map((t) => ({ value: t.value, label: t.label }))}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="2">
                    <FormGroup className="mb-0">
                      <Label for="fechaDesde">Fecha Inicio</Label>
                      <FDateInput
                        id="fechaDesde"
                        value={fechaDesde}
                        onChange={(e) => setFechaDesde(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="2">
                    <FormGroup className="mb-0">
                      <Label for="fechaHasta">Fecha Fin</Label>
                      <FDateInput
                        id="fechaHasta"
                        value={fechaHasta}
                        onChange={(e) => setFechaHasta(e.target.value)}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-0">
                      <Button
                        color="info"
                        block
                        onClick={consultar}
                        disabled={loading}
                        style={{ height: "40px", padding: "0" }}
                        title="Consultar"
                      >
                        {loading ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="tim-icons icon-zoom-split" />
                        )}
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Zona 3: Barra de acciones secundarias (Búsqueda y Exportación) */}
                <Row className="mb-4">
                  <Col md="5">
                    {listReporte.length > 0 && (
                      <FormGroup className="mb-0">
                        <Label for="searchQuery">Filtrar por DNI o Nombres en Resultados</Label>
                        <Input
                          type="text"
                          id="searchQuery"
                          placeholder="Escriba aquí para buscar..."
                          value={searchQuery}
                          onChange={handleSearch}
                          style={{ height: "40px" }}
                        />
                      </FormGroup>
                    )}
                  </Col>
                  <Col md="7" className="text-right d-flex align-items-end justify-content-end">
                    {listFiltered.length > 0 && (
                      <Button color="danger" className="btn-fill" onClick={exportarPDF}>
                        <i className="tim-icons icon-cloud-download-93 mr-2" />
                        Exportar a PDF
                      </Button>
                    )}
                  </Col>
                </Row>

                {mensaje && <p className="text-warning text-center my-4">{mensaje}</p>}

                {listFiltered.length > 0 && (
                  <>
                    <hr className="my-4" />
                    <Table responsive>

                      <thead className="text-primary">
                        <tr>
                          <th>N°</th>
                          <th>Fecha</th>
                          <th>Apellidos y Nombres</th>
                          <th>DNI</th>
                          <th>H.I.M.</th>
                          <th>Tardanza (min)</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((row, i) => (
                          <tr key={row._id || i}>
                            <td>{indexOfLast - itemsPerPage + i + 1}</td>
                            <td>{fmtDate(row.fecha)}</td>
                            <td>
                              {row.alumno
                                ? `${row.alumno.apellidopaterno} ${row.alumno.apellidomaterno}, ${row.alumno.nombres}`
                                : "-"}
                            </td>
                            <td>{row.alumno?.documentoidentidad || "-"}</td>
                            <td>{row.horaIngresoManiana || "-"}</td>
                            <td>{row.minutosTardeManiana ?? "-"}</td>
                            <td>{row.estado || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <CustomPagination
                      itemsPerPage={itemsPerPage}
                      totalItems={listFiltered.length}
                      paginate={setCurrentPage}
                      currentPage={currentPage}
                    />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  );
}

export default FaltasVirtual;
